// src/shared/errors/ErrorFactory.ts

import axios, { AxiosError } from 'axios';
import type { AppError } from './appError.types';
import { isAppError } from './appError.types';

type Response422Data = {
  fullMessage?: string;
  message?: string;
  detail?: Array<{ msg?: string; message?: string }> | string;
  error?: string;
};

function getValidationMessage(response: AxiosError['response']): string | null {
  const data = response?.data as Response422Data | undefined;
  if (!data || typeof data !== 'object') return null;

  if (data.fullMessage && typeof data.fullMessage === 'string') {
    return data.fullMessage;
  }
  if (data.message && typeof data.message === 'string') {
    return data.message;
  }

  const detail = data.detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail
      .map((d) => (typeof d === 'string' ? d : (d?.msg ?? d?.message)))
      .filter(Boolean);
    return messages.length > 0 ? messages.join(', ') : null;
  }
  if (typeof detail === 'string') return detail;
  if (data.error && typeof data.error === 'string') return data.error;

  return null;
}

export function createAppError(raw: unknown): AppError {
  if (isAppError(raw)) return raw;

  const fallbackMessage =
    raw instanceof Error ? raw.message : String(raw ?? 'Unknown error');

  if (!axios.isAxiosError(raw)) {
    return { kind: 'unknown', message: fallbackMessage };
  }

  const err = raw;

  if (err.response == null) {
    return {
      kind: 'network',
      message: err.message || 'Network error',
    };
  }

  const status = err.response.status;
  const statusText = err.response.statusText ?? err.message ?? 'Unknown error';

  if (status === 422) {
    const msg = getValidationMessage(err.response) ?? statusText;
    return { kind: 'validation', message: msg };
  }

  if (status === 400) {
    const msg = getValidationMessage(err.response) ?? statusText;
    return { kind: 'validation', message: msg };
  }

  if (status === 401) return { kind: 'auth', message: statusText };
  if (status === 403) return { kind: 'forbidden', message: statusText };
  if (status === 404) return { kind: 'notFound', message: statusText };

  if (status != null && status >= 500) {
    return { kind: 'server', message: statusText, status };
  }

  if (status != null && status >= 400 && status < 500) {
    return { kind: 'unknown', message: statusText };
  }

  return { kind: 'unknown', message: statusText };
}
